import { invokeTauri } from './bridge'
import { isInTauriShell } from './detector'
import type {
  ConnectThermalPrinterRequest,
  ConnectThermalPrinterResult,
  ThermalPrintJobRequest,
  ThermalPrintJobResult,
  ThermalPrinterInfo,
  ThermalPrinterStatus,
} from './types'

export async function listThermalPrinters(): Promise<ThermalPrinterInfo[]> {
  if (!isInTauriShell()) return []
  try {
    const result = await invokeTauri<{ printers: ThermalPrinterInfo[] }>('list_thermal_printers')
    return result.printers ?? []
  } catch {
    return []
  }
}

export async function connectThermalPrinter(
  request: ConnectThermalPrinterRequest
): Promise<ConnectThermalPrinterResult> {
  if (!isInTauriShell()) {
    return {
      success: false,
      connectionType: request.connectionType,
      address: request.address,
      deviceName: request.deviceName,
    }
  }
  return invokeTauri<ConnectThermalPrinterResult>('connect_thermal_printer', { request })
}

export async function getThermalPrinterStatus(): Promise<ThermalPrinterStatus> {
  if (!isInTauriShell()) {
    return { connected: false }
  }
  return invokeTauri<ThermalPrinterStatus>('get_thermal_printer_status')
}

export async function printThermalEscpos(
  request: ThermalPrintJobRequest
): Promise<ThermalPrintJobResult> {
  if (!isInTauriShell()) {
    return {
      jobId: request.jobId,
      success: false,
      error: 'Thermal print requires Android Tauri shell',
      errorCode: 'unsupported',
    }
  }
  try {
    return await invokeTauri<ThermalPrintJobResult>('print_thermal_escpos', { request })
  } catch (e) {
    return {
      jobId: request.jobId,
      success: false,
      error: e instanceof Error ? e.message : String(e),
      errorCode: 'bridge_error',
    }
  }
}

export async function printThermalTest(): Promise<{ success: boolean; error?: string }> {
  if (!isInTauriShell()) {
    return { success: false, error: 'Thermal print requires Android Tauri shell' }
  }
  try {
    return await invokeTauri<{ success: boolean; error?: string }>('print_thermal_test')
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : String(e) }
  }
}

/** True when bahelastruk should use ESC/POS thermal path instead of photo PrintUtils. */
export async function shouldUseThermalPrint(): Promise<boolean> {
  if (!isInTauriShell()) return false
  const { getRuntimeInfo } = await import('./getRuntimeInfo')
  const info = await getRuntimeInfo()
  return info.platform === 'android' && (info.capabilities?.thermalPrint ?? false)
}
